import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    Heading,
    VStack,
    HStack,
    Text,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    Input,
    Select,
    Switch,
    useToast,
    Divider,
    SimpleGrid,
} from "@chakra-ui/react";
import { FiCreditCard, FiSave } from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, brandConfig } from "../../brandConfig";

interface BillingDetails {
    companyName: string;
    taxId: string;
    billingEmail: string;
    billingPhone: string;
    billingAddress: {
        addressLine1: string;
        addressLine2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
    };
    invoicePreferences: {
        emailInvoices: boolean;
        autoPayEnabled: boolean;
        invoiceLanguage: string;
        taxIncluded: boolean;
    };
}

const BillingDetails: React.FC = () => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    
    // Consistent styling from brandConfig
    const bg = getColor("background.main");
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");

    // Mock data - in real app this would come from GraphQL
    const [billingDetails, setBillingDetails] = useState<BillingDetails>({
        companyName: "My Business Pty Ltd",
        taxId: "ABN 12 345 678 901",
        billingEmail: "billing@mybusiness.com",
        billingPhone: "+61 2 9876 5432",
        billingAddress: {
            addressLine1: "123 Business Street",
            addressLine2: "Suite 456",
            city: "Sydney",
            state: "NSW",
            postalCode: "2000",
            country: "Australia",
        },
        invoicePreferences: {
            emailInvoices: true,
            autoPayEnabled: false,
            invoiceLanguage: "en",
            taxIncluded: true,
        },
    });

    const handleInputChange = (section: keyof BillingDetails, field: string, value: string | boolean) => {
        if (section === "billingAddress" || section === "invoicePreferences") {
            setBillingDetails({
                ...billingDetails,
                [section]: {
                    ...billingDetails[section],
                    [field]: value,
                },
            });
        } else {
            setBillingDetails({
                ...billingDetails,
                [field]: value,
            });
        }
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // Mock save operation - in real app this would be a GraphQL mutation
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            toast({
                title: "Billing details updated",
                description: "Your billing information has been saved successfully.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error updating billing details",
                description: "Please try again later.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <Container maxW="container.lg" py={8} flex="1">
                <VStack spacing={6} align="stretch">
                    <Box>
                        <Heading size="lg" color={textPrimary} mb={2}>
                            💳 Billing Details
                        </Heading>
                        <Text color={textSecondary}>
                            Manage your billing information, company details, and invoice preferences.
                        </Text>
                    </Box>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {/* Company Information */}
                        <Card
                            bg={cardGradientBg}
                            backdropFilter="blur(10px)"
                            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                            border="1px"
                            borderColor={cardBorder}
                        >
                            <CardHeader borderBottom="1px" borderColor={cardBorder}>
                                <Heading size="md" color={textPrimary}>Company Information</Heading>
                            </CardHeader>
                            <CardBody>
                                <VStack spacing={4}>
                                    <FormControl>
                                        <FormLabel color={textPrimary}>Company Name</FormLabel>
                                        <Input
                                            value={billingDetails.companyName}
                                            onChange={(e) => handleInputChange("companyName" as keyof BillingDetails, "companyName", e.target.value)}
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel color={textPrimary}>Tax ID / ABN</FormLabel>
                                        <Input
                                            value={billingDetails.taxId}
                                            onChange={(e) => handleInputChange("taxId" as keyof BillingDetails, "taxId", e.target.value)}
                                            placeholder="ABN 12 345 678 901"
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel color={textPrimary}>Billing Email</FormLabel>
                                        <Input
                                            type="email"
                                            value={billingDetails.billingEmail}
                                            onChange={(e) => handleInputChange("billingEmail" as keyof BillingDetails, "billingEmail", e.target.value)}
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel color={textPrimary}>Billing Phone</FormLabel>
                                        <Input
                                            type="tel"
                                            value={billingDetails.billingPhone}
                                            onChange={(e) => handleInputChange("billingPhone" as keyof BillingDetails, "billingPhone", e.target.value)}
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>
                                </VStack>
                            </CardBody>
                        </Card>

                        {/* Billing Address */}
                        <Card
                            bg={cardGradientBg}
                            backdropFilter="blur(10px)"
                            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                            border="1px"
                            borderColor={cardBorder}
                        >
                            <CardHeader borderBottom="1px" borderColor={cardBorder}>
                                <Heading size="md" color={textPrimary}>Billing Address</Heading>
                            </CardHeader>
                            <CardBody>
                                <VStack spacing={4}>
                                    <FormControl>
                                        <FormLabel color={textPrimary}>Address Line 1</FormLabel>
                                        <Input
                                            value={billingDetails.billingAddress.addressLine1}
                                            onChange={(e) => handleInputChange("billingAddress", "addressLine1", e.target.value)}
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel color={textPrimary}>Address Line 2</FormLabel>
                                        <Input
                                            value={billingDetails.billingAddress.addressLine2 || ""}
                                            onChange={(e) => handleInputChange("billingAddress", "addressLine2", e.target.value)}
                                            placeholder="Suite, apartment, etc. (optional)"
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _placeholder={{ color: textMuted }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                            _hover={{
                                                borderColor: textSecondary
                                            }}
                                        />
                                    </FormControl>

                                    <HStack width="100%">
                                        <FormControl>
                                            <FormLabel color={textPrimary}>City</FormLabel>
                                            <Input
                                                value={billingDetails.billingAddress.city}
                                                onChange={(e) => handleInputChange("billingAddress", "city", e.target.value)}
                                                bg="rgba(0, 0, 0, 0.2)"
                                                border="1px"
                                                borderColor={cardBorder}
                                                color={textPrimary}
                                                _placeholder={{ color: textMuted }}
                                                _focus={{
                                                    borderColor: textSecondary,
                                                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                                }}
                                                _hover={{
                                                    borderColor: textSecondary
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel color={textPrimary}>State</FormLabel>
                                            <Select
                                                value={billingDetails.billingAddress.state}
                                                onChange={(e) => handleInputChange("billingAddress", "state", e.target.value)}
                                                bg="rgba(0, 0, 0, 0.2)"
                                                border="1px"
                                                borderColor={cardBorder}
                                                color={textPrimary}
                                                _hover={{ borderColor: textSecondary }}
                                                _focus={{
                                                    borderColor: textSecondary,
                                                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                                }}
                                            >
                                                <option value="NSW" style={{ backgroundColor: '#1a1a1a' }}>NSW</option>
                                                <option value="VIC" style={{ backgroundColor: '#1a1a1a' }}>VIC</option>
                                                <option value="QLD" style={{ backgroundColor: '#1a1a1a' }}>QLD</option>
                                                <option value="WA" style={{ backgroundColor: '#1a1a1a' }}>WA</option>
                                                <option value="SA" style={{ backgroundColor: '#1a1a1a' }}>SA</option>
                                                <option value="TAS" style={{ backgroundColor: '#1a1a1a' }}>TAS</option>
                                                <option value="ACT" style={{ backgroundColor: '#1a1a1a' }}>ACT</option>
                                                <option value="NT" style={{ backgroundColor: '#1a1a1a' }}>NT</option>
                                            </Select>
                                        </FormControl>
                                    </HStack>

                                    <HStack width="100%">
                                        <FormControl>
                                            <FormLabel color={textPrimary}>Postal Code</FormLabel>
                                            <Input
                                                value={billingDetails.billingAddress.postalCode}
                                                onChange={(e) => handleInputChange("billingAddress", "postalCode", e.target.value)}
                                                bg="rgba(0, 0, 0, 0.2)"
                                                border="1px"
                                                borderColor={cardBorder}
                                                color={textPrimary}
                                                _placeholder={{ color: textMuted }}
                                                _focus={{
                                                    borderColor: textSecondary,
                                                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                                }}
                                                _hover={{
                                                    borderColor: textSecondary
                                                }}
                                            />
                                        </FormControl>

                                        <FormControl>
                                            <FormLabel color={textPrimary}>Country</FormLabel>
                                            <Select
                                                value={billingDetails.billingAddress.country}
                                                onChange={(e) => handleInputChange("billingAddress", "country", e.target.value)}
                                                bg="rgba(0, 0, 0, 0.2)"
                                                border="1px"
                                                borderColor={cardBorder}
                                                color={textPrimary}
                                                _hover={{ borderColor: textSecondary }}
                                                _focus={{
                                                    borderColor: textSecondary,
                                                    boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                                }}
                                            >
                                                <option value="Australia" style={{ backgroundColor: '#1a1a1a' }}>Australia</option>
                                                <option value="New Zealand" style={{ backgroundColor: '#1a1a1a' }}>New Zealand</option>
                                                <option value="United States" style={{ backgroundColor: '#1a1a1a' }}>United States</option>
                                                <option value="Canada" style={{ backgroundColor: '#1a1a1a' }}>Canada</option>
                                                <option value="United Kingdom" style={{ backgroundColor: '#1a1a1a' }}>United Kingdom</option>
                                            </Select>
                                        </FormControl>
                                    </HStack>
                                </VStack>
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Invoice Preferences */}
                    <Card
                        bg={cardGradientBg}
                        backdropFilter="blur(10px)"
                        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                        border="1px"
                        borderColor={cardBorder}
                    >
                        <CardHeader borderBottom="1px" borderColor={cardBorder}>
                            <Heading size="md" color={textPrimary}>Invoice Preferences</Heading>
                        </CardHeader>
                        <CardBody>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                                <VStack spacing={4} align="stretch">
                                    <FormControl display="flex" alignItems="center">
                                        <FormLabel htmlFor="email-invoices" mb="0" color={textPrimary}>
                                            Email invoices automatically
                                        </FormLabel>
                                        <Switch
                                            id="email-invoices"
                                            isChecked={billingDetails.invoicePreferences.emailInvoices}
                                            onChange={(e) => handleInputChange("invoicePreferences", "emailInvoices", e.target.checked)}
                                            colorScheme="blue"
                                        />
                                    </FormControl>

                                    <FormControl display="flex" alignItems="center">
                                        <FormLabel htmlFor="auto-pay" mb="0" color={textPrimary}>
                                            Enable automatic payments
                                        </FormLabel>
                                        <Switch
                                            id="auto-pay"
                                            isChecked={billingDetails.invoicePreferences.autoPayEnabled}
                                            onChange={(e) => handleInputChange("invoicePreferences", "autoPayEnabled", e.target.checked)}
                                            colorScheme="blue"
                                        />
                                    </FormControl>

                                    <FormControl display="flex" alignItems="center">
                                        <FormLabel htmlFor="tax-included" mb="0" color={textPrimary}>
                                            Display prices with tax included
                                        </FormLabel>
                                        <Switch
                                            id="tax-included"
                                            isChecked={billingDetails.invoicePreferences.taxIncluded}
                                            onChange={(e) => handleInputChange("invoicePreferences", "taxIncluded", e.target.checked)}
                                            colorScheme="blue"
                                        />
                                    </FormControl>
                                </VStack>

                                <VStack spacing={4} align="stretch">
                                    <FormControl>
                                        <FormLabel color={textPrimary}>Invoice Language</FormLabel>
                                        <Select
                                            value={billingDetails.invoicePreferences.invoiceLanguage}
                                            onChange={(e) => handleInputChange("invoicePreferences", "invoiceLanguage", e.target.value)}
                                            bg="rgba(0, 0, 0, 0.2)"
                                            border="1px"
                                            borderColor={cardBorder}
                                            color={textPrimary}
                                            _hover={{ borderColor: textSecondary }}
                                            _focus={{
                                                borderColor: textSecondary,
                                                boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.1)"
                                            }}
                                        >
                                            <option value="en" style={{ backgroundColor: '#1a1a1a' }}>English</option>
                                            <option value="es" style={{ backgroundColor: '#1a1a1a' }}>Spanish</option>
                                            <option value="fr" style={{ backgroundColor: '#1a1a1a' }}>French</option>
                                            <option value="de" style={{ backgroundColor: '#1a1a1a' }}>German</option>
                                            <option value="zh" style={{ backgroundColor: '#1a1a1a' }}>Chinese</option>
                                        </Select>
                                    </FormControl>
                                </VStack>
                            </SimpleGrid>
                        </CardBody>
                    </Card>

                    <Divider borderColor={cardBorder} />

                    {/* Save Button */}
                    <HStack justify="flex-end">
                        <Button
                            leftIcon={<FiSave />}
                            bg="white"
                            color="black"
                            _hover={{ 
                                bg: "gray.100",
                                transform: "translateY(-2px)"
                            }}
                            onClick={handleSave}
                            isLoading={isLoading}
                            loadingText="Saving..."
                            size="lg"
                            boxShadow="0 2px 4px rgba(255, 255, 255, 0.1)"
                        >
                            Save Billing Details
                        </Button>
                    </HStack>
                </VStack>
            </Container>
            <FooterWithFourColumns />
        </Box>
    );
};

export default BillingDetails;